// src/packages/care/prediction/knowledgeBase.ts
//
// Literature-derived priors for the evidence-aware fertile-window predictor (issue #1908).
//
// These are POPULATION priors drawn from clinical guidelines and peer-reviewed cohort
// studies. The Bayesian engine (bayesianCycleModel.ts) uses them as priors and updates
// per-patient posteriors as logged cycles accumulate — so a patient with sparse history
// stays near these population values and personalizes as evidence arrives.
//
// This module is the single source of truth for every model constant, mirroring the
// published-coefficient pattern in clinics/prediction/ivfPredictionEngine.ts. Each value
// carries its source. Bump KNOWLEDGE_BASE_VERSION whenever a prior changes so predictions
// remain auditable/reproducible.
//
// Sources:
//   [ASRM 2021]   ASRM Practice Committee, "Optimizing natural fertility: a committee
//                 opinion", Fertil Steril 2022;117:53-63.
//   [Bull 2019]   Bull et al., "Real-world menstrual cycle characteristics of more than
//                 600,000 menstrual cycles", npj Digital Medicine 2019 (n=612,613 ovulatory
//                 cycles / 124,648 Natural Cycles app users).
//   [Fehring 2006] Fehring, Schneider & Raviele, "Variability in the Phases of the Menstrual
//                 Cycle", JOGNN 2006 (n=141 women, 1,060 cycles).
//   [Lenton 1984] Lenton et al., BJOG 1984 (luteal PMID 6743610, n=327; follicular
//                 PMID 6743609, n=293).
//   [Henry 2024]  Henry et al., Human Reproduction 2024 (n=53 women, 676 ovulatory cycles).
//   [Wilcox 2000] Wilcox, Dunson & Baird, BMJ 2000 (n=221 women, 696 cycles).
//
// Caveat: the largest source (Bull 2019) is a self-selected app cohort (predominantly
// well-educated, normal-BMI, Northern-European/US women) — treat these as app-cohort norms,
// not universal general-population values. Luteal means diverge by method/cohort
// (Bull 12.4d, Lenton 14.1d, Henry median 10.9d); the "~14-day fixed luteal" is a clinical
// convention, so luteal is modeled as a distribution, not a constant.

import type { FertileDayCategory } from "./fertileWindow.types";

/** Version stamp for the encoded priors (audit + reproducibility). */
export const KNOWLEDGE_BASE_VERSION = "2026-07-20";

/**
 * Menstrual cycle length (menses onset → next menses onset), in days.
 * Between-woman spread (populationSdDays) is the prior width; within-woman spread
 * (withinWomanSdDays) is the tighter cycle-to-cycle variability used for the
 * hierarchical (partial-pooling) posterior.
 */
export const CYCLE_LENGTH = {
  /** Population mean cycle length. [Bull 2019] */
  populationMeanDays: 29.3,
  /** Population (between-woman) SD. [Bull 2019] */
  populationSdDays: 5.2,
  /** Age at which populationMeanDays is anchored (Bull whole-population mean age). [Bull 2019] */
  ageAnchorYears: 29,
  /** Mean decline in cycle length per year of age across ~25–45y. [Bull 2019] */
  ageSlopeDaysPerYear: -0.18,
  /** 95% reference range for a normal cycle. [Fehring 2006] */
  normalRangeDays: { min: 22, max: 36 },
  /**
   * Plausible observed cycle-gap range (data-quality filter). Gaps outside this are treated
   * as logging errors or skipped-cycle artifacts and excluded from cycle statistics — wider
   * than the 95% normal range so genuinely long cycles are kept while doubled/missed-log gaps
   * (e.g. ~58 days) are rejected. Interim skipped-cycle handling pending SkipTrack-based modeling.
   */
  plausibleRangeDays: { min: 21, max: 45 },
  /** Within-woman (cycle-to-cycle) SD — hierarchical level-1 spread. [Bull 2019] */
  withinWomanSdDays: 2.6,
} as const;

/**
 * Luteal phase (ovulation → next menses), in days. Short and comparatively stable,
 * so it serves as the relatively-fixed offset for back-calculating ovulation from a
 * predicted next-period date. Modeled as a distribution, not a constant.
 */
export const LUTEAL_PHASE = {
  /** Prior mean — blend of Bull (12.4d) and Lenton (14.1d); ASRM convention ~14d. [Bull 2019; Lenton 1984; ASRM 2021] */
  meanDays: 13,
  /** Prior SD. [Bull 2019: 2.4d; Lenton 1984: 1.4d] */
  sdDays: 1.6,
  /** Luteal length ≤ this many days is clinically short (luteal-phase defect). [Lenton 1984; ASRM 2021] */
  shortThresholdDays: 9,
} as const;

/** Cycle-regularity classification derived from observed history. */
export const REGULARITY = {
  /** Within-woman range > this many days ⇒ classify cycles as irregular (widen interval, lower confidence). [Fehring 2006] */
  irregularRangeThresholdDays: 7,
} as const;

/**
 * Data-sufficiency tiers (count of usable observed cycles) → prediction eligibility.
 * Matches issue #1908's fallback policy and the "calendar prediction is unreliable"
 * evidence [ASRM 2021; Wilcox 2000]: no personalized estimate is shown below
 * minCyclesForPersonalized; full confidence requires minCyclesForFullConfidence
 * (quality checks permitting).
 */
export const DATA_SUFFICIENCY = {
  /** Fewer than this many observed cycles ⇒ no personalized estimate (educational state). */
  minCyclesForPersonalized: 2,
  /** At least this many observed cycles ⇒ eligible for full-confidence personalized model. */
  minCyclesForFullConfidence: 3,
} as const;

/**
 * Fertile window per ASRM: the 6-day interval ending on the day of ovulation — i.e.
 * ovulation day (offset 0) and the 5 preceding days. Day categories follow the verified
 * day-specific conception pattern: peak fecundability in the 2 days before ovulation,
 * declining on the day of ovulation itself. [ASRM 2021; Wilcox 2000]
 *
 * `dayCategories` maps each day offset relative to ovulation (0 = ovulation day, negative
 * = before) to a fertility category. The engine assigns per-day numeric probabilities from
 * these categories combined with the ovulation-posterior spread — this table intentionally
 * encodes only the guideline-verified category structure, not invented conception rates.
 */
export const FERTILE_WINDOW: {
  readonly windowLengthDays: number;
  readonly dayCategories: readonly {
    readonly dayOffset: number;
    readonly category: FertileDayCategory;
  }[];
} = {
  windowLengthDays: 6,
  dayCategories: [
    { dayOffset: -5, category: "possible" },
    { dayOffset: -4, category: "possible" },
    { dayOffset: -3, category: "likely" },
    { dayOffset: -2, category: "peak" },
    { dayOffset: -1, category: "peak" },
    { dayOffset: 0, category: "likely" },
  ],
};
